/**
 * Client-side prediction for your own blob.
 *
 * A blob's motion depends on nothing but its own input, so a remote player can
 * run it locally and be right almost every time. That is the whole trick: your
 * blob answers the keyboard immediately while the ball, which the host owns,
 * arrives on its own schedule.
 *
 * Reconciliation is the boring half. Every snapshot carries the authoritative
 * position of your blob at a known tick. If what we predicted for that tick
 * disagrees, snap to the truth and replay the inputs the host had not seen yet.
 * If it agrees -- the usual case -- do nothing, because snapping to an identical
 * state 30 times a second would only add jitter.
 *
 * Inputs are tagged with the tick they were applied on, and the guest stands on
 * the host's tick clock, so "unacked" is exactly "recorded after the snapshot's
 * tick". That is why no acknowledgement field is needed on the wire.
 */
import {
  type BlobInput,
  type BlobState,
  type Constants,
  type SimState,
  stepBlobMotion,
} from '@office/sim';

/** Spec'd depth: the last 60 local inputs. */
export const INPUT_HISTORY = 60;
/**
 * Below this the prediction counts as correct. A blob tops out around 7 m/s,
 * so one tick is ~0.06 m; 2 cm is comfortably inside a single tick of drift.
 */
export const RESYNC_THRESHOLD_METRES = 0.02;

interface InputRecord {
  tick: number;
  input: BlobInput;
}

export interface ReconcileResult {
  /** Distance between prediction and authority at the snapshot's tick. */
  error: number;
  /** Whether the blob was snapped back and inputs replayed. */
  resynced: boolean;
  /** How many inputs were replayed after the snap. */
  replayed: number;
}

function copyBlob(blob: BlobState): BlobState {
  return { ...blob };
}

export class BlobPredictor {
  /** The locally predicted blob. Render this, not the snapshot's copy. */
  blob: BlobState;
  /** Tick this prediction is standing on. Synced to the host by snapshots. */
  tick = 0;

  private inputs: InputRecord[] = [];
  private states = new Map<number, BlobState>();
  private lastResult: ReconcileResult = { error: 0, resynced: false, replayed: 0 };

  constructor(
    blob: BlobState,
    private readonly constants: Constants,
    private readonly netEnabled: boolean,
    startTick = 0,
  ) {
    this.blob = copyBlob(blob);
    this.tick = startTick;
  }

  get lastReconcile(): ReconcileResult {
    return this.lastResult;
  }

  /** Recorded inputs the host cannot have applied yet. */
  get pending(): number {
    return this.inputs.length;
  }

  /** Advance one fixed step under `input`, recording it so it can be replayed. */
  step(input: BlobInput): void {
    this.tick++;
    this.inputs.push({ tick: this.tick, input });
    while (this.inputs.length > INPUT_HISTORY) this.inputs.shift();

    stepBlobMotion(this.blob, input, this.constants, this.netEnabled);
    this.states.set(this.tick, copyBlob(this.blob));
    this.states.delete(this.tick - INPUT_HISTORY);
  }

  /** Take the authoritative state for this player's slot. */
  reconcile(state: SimState, slot: number): ReconcileResult {
    const authoritative = state.blobs[slot];
    if (!authoritative) {
      this.lastResult = { error: 0, resynced: false, replayed: 0 };
      return this.lastResult;
    }

    const predicted = this.states.get(state.tick);
    // Anything at or before the snapshot's tick is now history.
    this.inputs = this.inputs.filter((r) => r.tick > state.tick);

    if (!predicted) {
      // No prediction for that tick: out of history, or the first snapshot.
      const replayed = this.snapTo(authoritative, state.tick);
      this.lastResult = { error: Infinity, resynced: true, replayed };
      return this.lastResult;
    }

    const error = Math.hypot(predicted.x - authoritative.x, predicted.y - authoritative.y);
    if (error <= RESYNC_THRESHOLD_METRES) {
      this.lastResult = { error, resynced: false, replayed: 0 };
      return this.lastResult;
    }

    const replayed = this.snapTo(authoritative, state.tick);
    this.lastResult = { error, resynced: true, replayed };
    return this.lastResult;
  }

  /** Snap to authority, then replay everything recorded after `tick`. */
  private snapTo(authoritative: BlobState, tick: number): number {
    this.blob = copyBlob(authoritative);
    this.tick = tick;
    this.states.clear();
    this.states.set(tick, copyBlob(this.blob));

    const pending = this.inputs;
    for (const record of pending) {
      this.tick++;
      stepBlobMotion(this.blob, record.input, this.constants, this.netEnabled);
      this.states.set(this.tick, copyBlob(this.blob));
      record.tick = this.tick;
    }
    return pending.length;
  }
}
